import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Trash2, Plus, X, Loader2, Euro, AlertTriangle, CalendarDays,
  Search, Filter, RotateCcw, Sparkles, ArrowUpRight,
} from "lucide-react";
import api, { formatApiError } from "../api";
import {
  Button, Card, Input, Select, Label, Badge, Spinner, EmptyState,
} from "../components/ui/Primitives";
import { hasModule } from "../components/RouteGuards";
import { useAuth } from "../contexts/AuthContext";
import { formatEUR as fmtEur } from "../utils/format";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

function Kpi({ label, value, sub, tone = "default", testId }) {
  const accent = tone === "warn" ? "border-warn/30" : tone === "err" ? "border-err/30" : "border-white/10";
  return (
    <div className={`bg-bg-surface border ${accent} rounded-xl px-4 py-3`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-medium">{label}</div>
      <div className="num text-xl font-medium text-ink-primary tracking-tight mt-1">{value}</div>
      {sub && <div className="text-[10px] text-ink-muted mt-0.5">{sub}</div>}
    </div>
  );
}

function WasteForm({ meta, ingredients, recipes, onSaved, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    kind: "leftover",
    source: "ingredient",
    ingredient_id: "",
    recipe_id: "",
    quantity: "",
    unit: "g",
    reason: "",
    cost_override: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const qty = parseFloat(form.quantity);
    if (!qty || qty <= 0) { toast.error(t("waste.qty_required")); return; }
    const body = {
      kind: form.kind,
      quantity: qty,
      unit: form.unit,
      reason: form.reason || "",
    };
    if (form.source === "ingredient" && form.ingredient_id) body.ingredient_id = form.ingredient_id;
    else if (form.source === "recipe" && form.recipe_id) {
      body.recipe_id = form.recipe_id;
      body.unit = "Portion";
    } else if (form.source === "manual" && form.cost_override) {
      body.cost_override = parseFloat(form.cost_override);
    }
    setSaving(true);
    try {
      const { data } = await api.post("/waste", body);
      toast.success(t("waste.saved"));
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="waste-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>{t("waste.kind")}</Label>
          <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="waste-kind">
            {(meta.kinds || []).map((k) => <option key={k} value={k}>{t(`waste.kind_${k}`, k)}</option>)}
          </Select>
        </div>
        <div>
          <Label>{t("waste.source")}</Label>
          <Select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="waste-source">
            <option value="ingredient">{t("waste.source_ingredient")}</option>
            <option value="recipe">{t("waste.source_recipe")}</option>
            <option value="manual">{t("waste.source_manual")}</option>
          </Select>
        </div>
        <div className="md:col-span-1">
          {form.source === "ingredient" && (
            <>
              <Label>{t("waste.ingredient")}</Label>
              <Select value={form.ingredient_id} onChange={(e) => {
                const ing = ingredients.find((i) => i.id === e.target.value);
                setForm({ ...form, ingredient_id: e.target.value, unit: ing?.unit || form.unit });
              }} data-testid="waste-ingredient">
                <option value="">— {t("waste.choose")} —</option>
                {ingredients.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </Select>
            </>
          )}
          {form.source === "recipe" && (
            <>
              <Label>{t("waste.recipe")}</Label>
              <Select value={form.recipe_id} onChange={(e) => setForm({ ...form, recipe_id: e.target.value, unit: "Portion" })} data-testid="waste-recipe">
                <option value="">— {t("waste.choose")} —</option>
                {recipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
            </>
          )}
          {form.source === "manual" && (
            <>
              <Label>{t("waste.cost_override")}</Label>
              <Input type="number" step="0.01" min="0" value={form.cost_override} onChange={(e) => setForm({ ...form, cost_override: e.target.value })} placeholder="0,00" data-testid="waste-cost" />
            </>
          )}
        </div>
        <div>
          <Label>{t("waste.quantity")}</Label>
          <Input type="number" step="0.01" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="0" data-testid="waste-qty" />
        </div>
        <div>
          <Label>{t("waste.unit")}</Label>
          <Select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} disabled={form.source === "recipe"} data-testid="waste-unit">
            {(meta.units || []).map((u) => <option key={u} value={u}>{u}</option>)}
          </Select>
        </div>
        <div className="md:col-span-1">
          <Label>{t("waste.reason")}</Label>
          <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder={t("waste.reason_ph")} data-testid="waste-reason" />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
        <Button type="submit" disabled={saving} data-testid="waste-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {t("waste.add")}
        </Button>
      </div>
    </form>
  );
}

export default function WastePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState({ kinds: [], units: [] });
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ kind: "", period: "month" });
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const canAI = hasModule(user, "ai");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.kind) q.set("kind", filter.kind);
      if (filter.period === "week") q.set("start", new Date(Date.now() - 7 * 86400000).toISOString());
      else if (filter.period === "month") q.set("start", new Date(Date.now() - 30 * 86400000).toISOString());
      const [list, sum, m, ings, recs] = await Promise.all([
        api.get(`/waste?${q.toString()}`),
        api.get(`/waste/summary?period=${filter.period}`),
        api.get("/waste/meta"),
        api.get("/ingredients").catch(() => ({ data: { items: [] } })),
        api.get("/recipes").catch(() => ({ data: { items: [] } })),
      ]);
      setEntries(list.data.items || []);
      setSummary(sum.data);
      setMeta(m.data);
      setIngredients(ings.data.items || []);
      setRecipes(recs.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter.kind, filter.period]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("waste.delete_confirm"))) return;
    try {
      await api.delete(`/waste/${id}`);
      toast.success(t("waste.deleted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  /** Open AI Generator pre-filled with this top-loss ingredient.
   *  Matches by name (case-insensitive) against the loaded ingredients list. */
  const handleAskAI = (item) => {
    if (!item || item.kind !== "ingredient") {
      toast(t("waste.ai_only_ingredients"));
      return;
    }
    if (!canAI) {
      toast.error(t("waste.ai_no_plan"));
      return;
    }
    const target = ingredients.find((i) => (i.name || "").toLowerCase() === (item.name || "").toLowerCase());
    navigate("/ai", {
      state: {
        intent: "waste_reuse",
        ingredient_id: target?.id || null,
        ingredient_name: item.name,
      },
    });
  };

  const kpis = useMemo(() => {
    if (!summary) return null;
    return {
      count: summary.count,
      cost: summary.total_cost,
      top: (summary.top_items || [])[0] || null,
    };
  }, [summary]);

  return (
    <div className="space-y-6 fade-in" data-testid="waste-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-warn mb-1 flex items-center gap-2">
            <Trash2 size={12} /> {t("waste.kicker")}
          </div>
          <h1 className="text-2xl font-semibold text-ink-primary">{t("waste.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1 max-w-xl">{t("waste.subtitle")}</p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)} data-testid="waste-toggle-form">
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? t("common.cancel") : t("waste.add")}
        </Button>
      </header>

      {showForm && (
        <Card className="p-5">
          <WasteForm
            meta={meta}
            ingredients={ingredients}
            recipes={recipes}
            onSaved={() => { setShowForm(false); load(); }}
            onClose={() => setShowForm(false)}
          />
        </Card>
      )}

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label={t("waste.kpi_count")} value={kpis.count} testId="waste-kpi-count" />
          <Kpi label={t("waste.kpi_cost")} value={fmtEur(kpis.cost)} tone={kpis.cost > 100 ? "warn" : "default"} testId="waste-kpi-cost" />
          <button
            type="button"
            onClick={() => kpis.top && handleAskAI(kpis.top)}
            disabled={!kpis.top || kpis.top.kind !== "ingredient"}
            className="text-left bg-bg-surface border border-white/10 rounded-xl px-4 py-3 hover:border-brand/40 hover:bg-brand/5 transition-all disabled:opacity-100 disabled:cursor-default disabled:hover:border-white/10 disabled:hover:bg-bg-surface group"
            data-testid="waste-kpi-top"
          >
            <div className="text-[10px] uppercase tracking-wider text-ink-muted font-medium flex items-center gap-1">
              {t("waste.kpi_top")}
              {kpis.top?.kind === "ingredient" && (
                <Sparkles size={10} className="text-brand opacity-60 group-hover:opacity-100 transition" />
              )}
            </div>
            <div className="num text-xl font-medium text-ink-primary tracking-tight mt-1 truncate">{kpis.top ? kpis.top.name : "—"}</div>
            <div className="text-[10px] text-ink-muted mt-0.5">
              {kpis.top ? `${fmtEur(kpis.top.cost)} · ${kpis.top.kind === "ingredient" ? t("waste.ai_hint_click") : t("waste.recipe_label")}` : ""}
            </div>
          </button>
          <Kpi label={t("waste.kpi_period")} value={t(`waste.period_${filter.period}`)} testId="waste-kpi-period" />
        </div>
      )}

      {/* Top loss items — click to ask AI for reuse ideas */}
      {summary && (summary.top_items || []).length > 0 && (
        <Card className="p-4" data-testid="waste-top-items">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-medium flex items-center gap-1.5">
              <Sparkles size={12} className="text-brand" /> {t("waste.top_items_title")}
            </div>
            <span className="text-[11px] text-ink-muted">{t("waste.top_items_hint")}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {summary.top_items.slice(0, 6).map((it, idx) => (
              <button
                key={`${it.name}-${it.kind}-${idx}`}
                type="button"
                onClick={() => handleAskAI(it)}
                disabled={it.kind !== "ingredient" || !canAI}
                className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-bg-void border border-white/10 hover:border-brand/40 hover:bg-brand/5 transition-all text-left disabled:opacity-60 disabled:cursor-default group"
                data-testid={`waste-top-item-${idx}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink-primary font-medium truncate">{it.name}</div>
                  <div className="text-[11px] text-ink-muted">{fmtEur(it.cost)} · {it.count} {t("waste.times")}</div>
                </div>
                {it.kind === "ingredient" && canAI ? (
                  <ArrowUpRight size={14} className="text-brand opacity-50 group-hover:opacity-100 transition shrink-0" />
                ) : (
                  <Badge tone="default" className="text-[9px]">{t(`waste.item_kind_${it.kind}`, it.kind)}</Badge>
                )}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label>{t("waste.filter_period")}</Label>
          <Select value={filter.period} onChange={(e) => setFilter({ ...filter, period: e.target.value })} data-testid="waste-filter-period">
            <option value="week">{t("waste.period_week")}</option>
            <option value="month">{t("waste.period_month")}</option>
            <option value="all">{t("waste.period_all")}</option>
          </Select>
        </div>
        <div>
          <Label>{t("waste.filter_kind")}</Label>
          <Select value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })} data-testid="waste-filter-kind">
            <option value="">{t("common.all")}</option>
            {(meta.kinds || []).map((k) => <option key={k} value={k}>{t(`waste.kind_${k}`, k)}</option>)}
          </Select>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setFilter({ kind: "", period: "month" })} data-testid="waste-filter-reset">
          <RotateCcw size={12} /> {t("waste.reset_filter")}
        </Button>
      </div>

      {/* Entries table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Spinner size={20} /></div>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={Trash2}
            title={t("waste.empty_title")}
            description={t("waste.empty_sub")}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="waste-table">
              <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">{t("waste.col_date")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("waste.col_kind")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("waste.col_item")}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t("waste.col_qty")}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t("waste.col_cost")}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t("waste.col_reason")}</th>
                  <th className="text-right px-4 py-2.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`waste-row-${e.id}`}>
                    <td className="px-4 py-3 text-ink-secondary whitespace-nowrap">{fmtDate(e.date)}</td>
                    <td className="px-4 py-3"><Badge>{t(`waste.kind_${e.kind}`, e.kind)}</Badge></td>
                    <td className="px-4 py-3 text-ink-primary">{e.item_name}</td>
                    <td className="px-4 py-3 num text-right text-ink-secondary">{e.quantity} {e.unit}</td>
                    <td className="px-4 py-3 num text-right text-err font-medium">{fmtEur(e.cost)}</td>
                    <td className="px-4 py-3 text-ink-secondary truncate max-w-[200px]">{e.reason || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleDelete(e.id)} className="text-ink-muted hover:text-err p-1" data-testid={`waste-delete-${e.id}`}>
                        <X size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
